/**
 * PO list KPI + Kanban helpers (A9) — pure, no UI.
 * Counts only from real PurchaseOrder rows; no invented statuses.
 */
import type { PoStatus, PurchaseOrder } from '@/services/procurement'

/** Column order = existing PoStatus union — do not add new statuses. */
export const PO_KANBAN_COLUMNS: PoStatus[] = [
  'DRAFT',
  'PENDING_APPROVAL',
  'APPROVED',
  'SENT',
  'PARTIAL',
  'RECEIVED',
  'CANCELLED',
  'SHORT',
]

const OPEN_STATUSES: ReadonlySet<PoStatus> = new Set([
  'DRAFT',
  'PENDING_APPROVAL',
  'APPROVED',
  'SENT',
  'PARTIAL',
])

const RECEIVING_STATUSES: ReadonlySet<PoStatus> = new Set(['SENT', 'PARTIAL'])

/** Overdue only after commitment to deliver — not draft/pending/closed/short. */
const OVERDUE_ELIGIBLE: ReadonlySet<PoStatus> = new Set([
  'APPROVED',
  'SENT',
  'PARTIAL',
])

export type PoListKpis = {
  openCount: number
  pendingApprovalCount: number
  receivingCount: number
  overdueDeliveryCount: number
}

/**
 * Sum of lineTotal. Proto sets lineTotal = unitPriceActual * qty (before VAT).
 */
export function poTotal(po: PurchaseOrder): number {
  return po.lines.reduce((a, l) => a + l.lineTotal, 0)
}

/**
 * PO total with VAT for lines whose VAT rate is known.
 *
 * UNKNOWN VAT is deliberately not treated as 0%: its VAT amount is omitted,
 * and callers must use poHasUnknownVat() to avoid presenting this as a
 * complete payable total.
 */
export function poTotalIncludingKnownVat(po: PurchaseOrder): number {
  return po.lines.reduce((total, line) => {
    if (line.vatRateStatus === 'UNKNOWN') return total + line.lineTotal
    const vatRate = line.vat ?? 0
    return total + line.lineTotal * (1 + vatRate / 100)
  }, 0)
}

export const PO_TOTAL_LABEL = 'Tổng (chưa VAT)'
export const PO_OPEN_TOTAL_LABEL = 'Đã gồm VAT đã biết'

/**
 * Average per-line receive ratio — never sum qty across different UOMs.
 * Each line contributes min(received/qty, 1); lines with qty<=0 are skipped.
 */
export function poReceiveProgress(po: PurchaseOrder): number {
  const lines = po.lines.filter((l) => l.qty > 0)
  if (!lines.length) return 0
  let sum = 0
  for (const l of lines) {
    sum += Math.min(l.receivedQty / l.qty, 1)
  }
  return sum / lines.length
}

export function formatReceivePct(ratio: number): string {
  return `Nhận ${Math.round(ratio * 100)}%`
}

export function poHasUnknownVat(po: PurchaseOrder): boolean {
  return po.lines.some((l) => l.vatRateStatus === 'UNKNOWN')
}

/** YYYY-MM-DD — overdue only for APPROVED / SENT / PARTIAL. */
export function isPoDeliveryOverdue(
  po: PurchaseOrder,
  asOfDate: string,
): boolean {
  if (!OVERDUE_ELIGIBLE.has(po.status)) return false
  if (!po.deliveryDate) return false
  return po.deliveryDate < asOfDate
}

export function computePoListKpis(
  orders: PurchaseOrder[],
  asOfDate: string,
): PoListKpis {
  let openCount = 0
  let pendingApprovalCount = 0
  let receivingCount = 0
  let overdueDeliveryCount = 0
  for (const po of orders) {
    if (OPEN_STATUSES.has(po.status)) openCount += 1
    if (po.status === 'PENDING_APPROVAL') pendingApprovalCount += 1
    if (RECEIVING_STATUSES.has(po.status)) receivingCount += 1
    if (isPoDeliveryOverdue(po, asOfDate)) overdueDeliveryCount += 1
  }
  return {
    openCount,
    pendingApprovalCount,
    receivingCount,
    overdueDeliveryCount,
  }
}

export function groupPosByStatus(
  orders: PurchaseOrder[],
): Record<PoStatus, PurchaseOrder[]> {
  const out = Object.fromEntries(
    PO_KANBAN_COLUMNS.map((s) => [s, [] as PurchaseOrder[]]),
  ) as Record<PoStatus, PurchaseOrder[]>
  for (const po of orders) {
    out[po.status]?.push(po)
  }
  return out
}
